import React, { useEffect, useState, useRef } from 'react';
import supabase from '../../../supabaseClient';
import { FaMicrophone, FaVideo, FaPaperclip, FaEllipsisV, FaSmile, FaTimes } from 'react-icons/fa';
import HoverCard from '../../../components/HoverCard';

// Add CSS variables for styling
const styles = {
  commentModalHeader: {
    backgroundColor: 'var(--surface)',
    borderRadius: '12px 12px 0 0',
    padding: '16px',
    borderBottom: '2px solid var(--primary-light)',
    boxShadow: 'var(--shadow-md)',
    marginBottom: '10px'
  },
  replyIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    backgroundColor: 'var(--glass-bg)',
    borderRadius: '8px 8px 0 0',
    marginBottom: '-1px',
    borderTop: '2px solid var(--primary-light)',
    borderLeft: '2px solid var(--primary-light)',
    borderRight: '2px solid var(--primary-light)',
    boxShadow: 'var(--shadow-sm)'
  },
  cancelButton: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'var(--glass-bg)'
    }
  },
  commentContainer: {
    backgroundColor: 'var(--card-bg)',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all 0.2s ease'
  },
  replyContainer: {
    backgroundColor: 'var(--glass-bg)',
    borderRadius: '8px',
    padding: '10px',
    marginTop: '8px',
    marginBottom: '8px',
    border: '1px solid var(--border-light)',
    borderLeft: '3px solid var(--primary-light)'
  }
};

const reactionsStyles = document.createElement('style');
reactionsStyles.innerHTML = `
  .active-reaction {
    background-color: var(--primary-light) !important;
    color: var(--text-light) !important;
    border-color: var(--accent) !important;
    transform: scale(1.05);
    box-shadow: var(--shadow-sm);
  }
  .react-btn.active-reaction {
    color: var(--accent) !important;
    background-color: var(--glass-bg) !important;
  }
  .react-btn {
    padding: 6px 10px;
    border-radius: 20px;
    border: 1px solid var(--border-light);
    background-color: var(--glass-bg);
    color: var(--text);
    transition: all 0.2s ease;
  }
  .react-btn:hover {
    background-color: var(--input-bg);
  }
  .reactions-popover {
    padding: 8px !important;
    border-radius: 20px !important;
    background-color: var(--card-bg) !important;
    border: 1px solid var(--border) !important;
    box-shadow: var(--shadow-md) !important;
  }
  .reaction-emoji-btn {
    padding: 6px 10px !important;
    border-radius: 16px !important;
    transition: all 0.2s ease !important;
    color: var(--text) !important;
  }
  .reaction-emoji-btn:hover {
    background-color: var(--glass-bg) !important;
    transform: scale(1.1);
  }
`;
document.head.appendChild(reactionsStyles);

const Comments = ({ postId, user, showOnlyInput = false, onCommentAdded, replyTo = null, openReplyModal }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentContent, setEditedCommentContent] = useState('');
  const [recordingType, setRecordingType] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStream, setRecordingStream] = useState(null);
  const [userProfiles, setUserProfiles] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const mediaInputRef = useRef();

  useEffect(() => {
    fetchComments();
  }, [postId]);

  async function fetchComments() {
    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    
    if (!error && data) {
      setComments(data);
      // Fetch user profiles for all comment authors
      const userIds = [...new Set(data.map(comment => comment.user_id))];
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', userIds);
        if (profiles) {
          const profileMap = {};
          profiles.forEach(profile => {
            profileMap[profile.id] = profile;
          });
          setUserProfiles(profileMap);
        }
      }
    }
  }

  async function handleAddComment(e) {
    e.preventDefault();
    if (!newComment.trim() && !mediaFile) return;
    let uploadedUrl = null;
    let uploadedType = null;
    if (mediaFile) {
      const ext = mediaFile.name.split('.').pop();
      const filePath = `comments/${user.id}/${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;
      let { error: uploadError } = await supabase.storage
        .from('comment-media')
        .upload(filePath, mediaFile, { cacheControl: '3600', upsert: false });
      if (uploadError) {
        alert('Failed to upload media');
        return;
      }
      const { data: publicUrlData } = supabase
        .storage
        .from('comment-media')
        .getPublicUrl(filePath);
      uploadedUrl = publicUrlData.publicUrl;
      uploadedType = mediaType;
    }
    const { error } = await supabase
      .from('comments')
      .insert([{
        post_id: postId,
        user_id: user.id,
        parent_id: replyTo,
        content: newComment,
        media_url: uploadedUrl,
        media_type: uploadedType
      }]);
    if (!error) {
      setNewComment('');
      setMediaFile(null);
      setMediaPreview(null);
      setMediaType(null);
      fetchComments();
      if (onCommentAdded) onCommentAdded();
    }
  }

  const startRecording = async (type) => {
    setRecordingType(type);
    setRecordedChunks([]);
    let constraints = type === 'audio' ? { audio: true, video: false } : { audio: true, video: true };
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setRecordingStream(stream);
      const mediaRecorder = new window.MediaRecorder(stream);
      setRecorder(mediaRecorder);
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          setRecordedChunks(prev => [...prev, e.data]);
        }
      };
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        setRecordingStream(null);
        setRecorder(null);
        const blob = new Blob(recordedChunks, { type: type === 'audio' ? 'audio/webm' : 'video/webm' });
        const file = new File([blob], `recording_${Date.now()}.webm`, { type: blob.type });
        setMediaFile(file);
        setMediaType(type);
        setMediaPreview(URL.createObjectURL(blob));
        setRecordedChunks([]);
        setRecordingType(null);
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      alert('Could not access your microphone/camera.');
      setIsRecording(false);
      setRecordingType(null);
    }
  };

  const stopRecording = () => {
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  };

  async function handleEditComment(commentId) {
    if (!editedCommentContent.trim()) return;
    const { error } = await supabase
      .from('comments')
      .update({ content: editedCommentContent })
      .eq('id', commentId);
    if (!error) {
      setEditingCommentId(null);
      setEditedCommentContent('');
      fetchComments();
    }
  }

  async function handleDeleteComment(commentId) {
    const confirmed = window.confirm('Are you sure you want to delete this comment? This action cannot be undone.');
    if (!confirmed) return;
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId);
    if (!error) {
      fetchComments();
    }
  }

  const reactionOptions = [
    { type: 'like', label: '👍' },
    { type: 'love', label: '❤️' },
    { type: 'funny', label: '😂' },
    { type: 'wow', label: '😮' },
    { type: 'sad', label: '😢' },
    { type: 'clap', label: '👏' }
  ];
  const [reactionsMap, setReactionsMap] = useState({});
  const [userReactions, setUserReactions] = useState({});
  const [showReactions, setShowReactions] = useState({});
  const [menuOpen, setMenuOpen] = useState({});

  async function fetchReactionsForComments() {
    const ids = comments.map(c => c.id);
    if (ids.length === 0) return;
    const { data, error } = await supabase
      .from('comment_reactions')
      .select('*')
      .in('comment_id', ids);
    if (!error) {
      const map = {};
      const userReactMap = {};
      data.forEach(r => {
        if (!map[r.comment_id]) map[r.comment_id] = {};
        map[r.comment_id][r.reaction_type] = (map[r.comment_id][r.reaction_type] || 0) + 1;
        if (r.user_id === user.id) {
          userReactMap[r.comment_id] = r.reaction_type;
        }
      });
      setReactionsMap(map);
      setUserReactions(userReactMap);
    }
  }
  useEffect(() => {
    fetchComments();
  }, [postId]);
  useEffect(() => {
    fetchReactionsForComments();
  }, [comments]);
  async function handleReact(commentId, reactionType) {
    if (userReactions[commentId] === reactionType) {
      await supabase
        .from('comment_reactions')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.id);
      setUserReactions(prev => {
        const newUserReactions = { ...prev };
        delete newUserReactions[commentId];
        return newUserReactions;
      });
    } else {
      await supabase.from('comment_reactions').upsert({
        comment_id: commentId,
        user_id: user.id,
        reaction_type: reactionType
      });
      setUserReactions(prev => ({
        ...prev,
        [commentId]: reactionType
      }));
    }
    fetchReactionsForComments();
    setShowReactions(prev => ({ ...prev, [commentId]: false }));
  }

  // Count replies for a comment
  function countReplies(commentId) {
    return comments.filter(c => c.parent_id === commentId).length;
  }

  // Toggle expanded state for a comment
  function toggleExpanded(commentId) {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  }

  function renderComments(parentId = null, level = 0) {
    const filteredComments = comments.filter(c => c.parent_id === parentId);
    return filteredComments.map(comment => (
        <div
          key={comment.id}
          style={{
            ...((level === 0) ? styles.commentContainer : styles.replyContainer),
            marginLeft: level * 24,
            marginTop: level === 0 ? 16 : 8,
            textAlign: 'left',
            position: 'relative'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <HoverCard type="profile" data={userProfiles[comment.user_id] || { id: comment.user_id }} currentUserId={user?.id}>
                <a href={`/app/profile/${comment.user_id}`} className="author-name-link" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <b>{userProfiles[comment.user_id]?.display_name || 'User'}</b>
                </a>
              </HoverCard>:
              {editingCommentId === comment.id ? (
                <>
                  <textarea
                    placeholder="Write your comment here... (Markdown supported, media optional)"
                    value={editedCommentContent}
                    onChange={e => setEditedCommentContent(e.target.value)}
                    style={{
                      width: '98%',
                      minHeight: 100,
                      marginLeft: 8,
                      borderRadius: 8,
                      border: '1px solid var(--primary-light)',
                      fontSize: 16,
                      padding: '10px 14px',
                      textAlign: 'left',
                      background: 'var(--input-bg)',
                      color: 'var(--text)',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 16, width: '100%', marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => handleEditComment(comment.id)}
                      style={{ minWidth: 90, padding: '8px 18px', fontSize: 16, borderRadius: 6, border: 'none', background: 'var(--primary-light)', color: '#fff', cursor: 'pointer', margin: 0 }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCommentId(null);
                        setEditedCommentContent('');
                      }}
                      style={{ minWidth: 90, padding: '8px 18px', fontSize: 16, borderRadius: 6, border: 'none', background: '#d32f2f', color: '#fff', cursor: 'pointer', margin: 0 }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <span style={{ marginLeft: 8 }}>{comment.content}</span>
              )}
              {comment.media_url && (
                <span style={{ marginLeft: 8 }}>
                  {comment.media_type === 'audio' ? (
                    <audio src={comment.media_url} controls />
                  ) : comment.media_type === 'video' ? (
                    <video src={comment.media_url} controls style={{ maxWidth: 200 }} />
                  ) : (
                    <a href={comment.media_url} target="_blank" rel="noopener noreferrer">Attachment</a>
                  )}
                </span>
              )}
              <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>{new Date(comment.created_at).toLocaleString()}</span>
              {/* Reactions display */}
              <span style={{ marginLeft: 8 }}>
                {reactionsMap[comment.id] && Object.entries(reactionsMap[comment.id]).map(([type, count]) => (
                  <span key={type} style={{ marginRight: 4 }}>{reactionOptions.find(r => r.type === type)?.label} {count}</span>
                ))}
              </span>
            </div>
            {/* Menu icon for edit/delete with danger zone */}
            {user.id === comment.user_id && editingCommentId !== comment.id && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, fontSize: 18, fontWeight: 'bold', color: 'inherit' }}
                  onClick={() => setMenuOpen(prev => ({ ...prev, [comment.id]: !prev[comment.id] }))}
                >
                  ⋮
                </button>
                {menuOpen[comment.id] && (
                  <div style={{ position: 'absolute', right: 0, top: 24, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 10, boxShadow: 'var(--shadow-md)', minWidth: 180, padding: '4px 0' }}>
                    <button
                      type="button"
                      style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text)', textAlign: 'left', transition: 'background 0.2s ease' }}
                      onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditedCommentContent(comment.content);
                        setMenuOpen(prev => ({ ...prev, [comment.id]: false }));
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--glass-bg)')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      Edit
                    </button>
                    <div style={{ padding: '4px 12px 2px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(248,113,113,0.9)' }}>
                      Danger zone
                    </div>
                    <button
                      type="button"
                      style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: '#b91c1c', color: '#fee2e2', cursor: 'pointer', textAlign: 'left', borderRadius: '0 0 6px 6px', marginTop: 2 }}
                      onClick={() => {
                        handleDeleteComment(comment.id);
                        setMenuOpen(prev => ({ ...prev, [comment.id]: false }));
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#7f1d1d')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Actions row: React, Reply */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4 }}>
            <div
              className="react-btn-wrapper"
              style={{ position: 'relative' }}
              onMouseEnter={() => setShowReactions(prev => ({ ...prev, [comment.id]: true }))}
              onMouseLeave={() => setShowReactions(prev => ({ ...prev, [comment.id]: false }))}
            >
              <button
                type="button"
                className={`react-btn ${userReactions[comment.id] ? 'active-reaction' : ''}`}
                style={{ fontSize: 14 }}
              >
                {userReactions[comment.id] ? 
                  reactionOptions.find(opt => opt.type === userReactions[comment.id])?.label : 
                  <FaSmile />}
              </button>
              {showReactions[comment.id] && (
                <div className="reactions-popover" style={{ position: 'absolute', top: 28, left: 0, background: '#fff', border: '1px solid #ccc', borderRadius: 4, zIndex: 20, display: 'flex', gap: 4, padding: 4 }}>
                  {reactionOptions.map(opt => (
                    <button
                      key={opt.type}
                      className={`reaction-emoji-btn ${userReactions[comment.id] === opt.type ? 'active-reaction' : ''}`}
                      style={{ 
                        fontSize: 18, 
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}
                      onClick={() => handleReact(comment.id, opt.type)}
                    >
                      {opt.label} 
                      {reactionsMap[comment.id]?.[opt.type] ? 
                        <span style={{ fontSize: '0.7rem', fontWeight: 'bold', minWidth: '1rem' }}>
                          {reactionsMap[comment.id][opt.type]}
                        </span> : 
                        ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => openReplyModal && openReplyModal(comment.id)}
              style={{ 
                fontSize: 14, 
                background: 'rgba(99, 102, 241, 0.1)', 
                border: '1px solid rgba(99, 102, 241, 0.2)', 
                borderRadius: '20px',
                padding: '6px 12px',
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 10h10a5 5 0 0 1 5 5v4M3 10l5 5M3 10l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Reply
            </button>
            {level === 0 && countReplies(comment.id) > 0 && (
              <button
                type="button"
                onClick={() => toggleExpanded(comment.id)}
                style={{ 
                  fontSize: 14, 
                  background: 'var(--glass-bg)', 
                  border: '1px solid var(--border-light)', 
                  borderRadius: '20px',
                  padding: '6px 12px',
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--input-bg)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--glass-bg)'}
              >
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    transform: expandedComments[comment.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {expandedComments[comment.id] ? 'Hide' : 'Show'} {countReplies(comment.id)} {countReplies(comment.id) === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>
          {/* Render child comments conditionally based on level and expanded state */}
          {(level > 0 || expandedComments[comment.id]) && renderComments(comment.id, level + 1)}
        </div>
      ));
  }

  // Function to get the username of the person being replied to
  const getReplyToUsername = () => {
    if (!replyTo) return null;
    const replyComment = comments.find(c => c.id === replyTo);
    if (!replyComment) return null;
    return userProfiles[replyComment.user_id]?.display_name || 'User';
  };

  // Function to cancel reply mode
  const cancelReply = () => {
    if (openReplyModal) {
      openReplyModal(null);
    }
  };

  if (showOnlyInput) {
    return (
      <div className="comment-modal-header" style={styles.commentModalHeader}>
        <form onSubmit={handleAddComment} style={{ width: '100%' }}>
          {replyTo && (
            <div style={styles.replyIndicator}>
              <span style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 17L4 12M4 12L9 7M4 12H20" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Replying to <strong>{getReplyToUsername()}</strong>
              </span>
              <button 
                type="button" 
                onClick={cancelReply}
                style={styles.cancelButton}
              >
                Cancel <FaTimes size={12} />
              </button>
            </div>
          )}
          <textarea
            placeholder={replyTo ? `Write a reply...` : "Write a comment..."}
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            style={{ 
              width: '100%', 
              minHeight: replyTo ? 100 : 120, 
              borderRadius: replyTo ? '0 0 8px 8px' : 8, 
              border: '2px solid var(--primary-light)', 
              fontSize: 16, 
              padding: '14px 18px 80px 18px', 
              textAlign: 'left',
              background: replyTo ? 'var(--glass-bg)' : 'var(--input-bg)',
              color: 'var(--text)',
              resize: 'vertical',
              boxSizing: 'border-box',
              borderTop: replyTo ? '0' : '2px solid var(--primary-light)',
              boxShadow: replyTo ? 'var(--shadow-md)' : 'var(--shadow-sm)',
              transition: 'all 0.2s ease',
              '&:focus': {
                outline: 'none',
                borderColor: 'var(--primary)',
                boxShadow: '0 0 0 2px var(--accent)'
              }
            }}
          />
        {mediaPreview && (
          <div style={{ marginTop: 8 }}>
            {mediaType === 'audio' ? (
              <audio src={mediaPreview} controls />
            ) : mediaType === 'video' ? (
              <video src={mediaPreview} controls style={{ maxWidth: 200 }} />
            ) : null}
            <button type="button" onClick={() => { setMediaFile(null); setMediaPreview(null); setMediaType(null); }} style={{ marginLeft: 8 }}>Remove</button>
          </div>
        )}
        <div style={{ 
          position: 'relative',
          marginTop: 12,
          width: '100%',
          marginBottom: 10
        }}>
          <div style={{ 
            position: 'absolute', 
            left: '10px', 
            bottom: '25px', 
            display: 'flex', 
            gap: 25, 
            alignItems: 'center',
            zIndex: 5
          }}>
            <button 
              type="button" 
              onClick={() => mediaInputRef.current.click()} 
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            >
              <FaPaperclip />
            </button>
            <input type="file" ref={mediaInputRef} style={{ display: 'none' }} accept="audio/*,video/*" onChange={e => {
              const file = e.target.files[0];
              if (file) {
                setMediaFile(file);
                setMediaType(file.type.startsWith('audio') ? 'audio' : file.type.startsWith('video') ? 'video' : null);
                setMediaPreview(URL.createObjectURL(file));
              }
            }} />
            <button 
              type="button" 
              onClick={() => startRecording('audio')} 
              disabled={isRecording}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            > 
              <FaMicrophone /> 
            </button>
            <button 
              type="button" 
              onClick={() => startRecording('video')} 
              disabled={isRecording}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            > 
              <FaVideo /> 
            </button>
            {isRecording && 
              <button 
                type="button" 
                onClick={stopRecording}
                style={{ background: 'var(--error)', border: 'none', color: 'var(--text-light)', padding: '4px 8px', borderRadius: 4, cursor: 'pointer' }}
              >
                Stop
              </button>
            }
          </div>
          <button 
            type="submit" 
            style={{ 
              position: 'absolute',
              right: '10px',
              bottom: '25px',
              background: 'var(--primary-light)', 
              color: 'white', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 16px', 
              cursor: 'pointer',
              fontWeight: 'bold',
              zIndex: 5
            }}
          >
            Send
          </button>
        </div>
      </form>
    </div>
    );
  }

  return (
    <>
      <div className="comment-modal-header">
        <form onSubmit={handleAddComment} style={{ width: '100%' }}>
          {replyTo && (
            <div style={styles.replyIndicator}>
              <span style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 17L4 12M4 12L9 7M4 12H20" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Replying to <strong>{getReplyToUsername()}</strong>
              </span>
              <button 
                type="button" 
                onClick={cancelReply}
                style={styles.cancelButton}
              >
                Cancel <FaTimes size={12} />
              </button>
            </div>
          )}
          <textarea
            placeholder={replyTo ? `Write a reply...` : "Write a comment..."}
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            style={{ 
              width: '100%', 
              minHeight: replyTo ? 100 : 120, 
              borderRadius: replyTo ? '0 0 8px 8px' : 8, 
              border: '2px solid var(--primary-light)', 
              fontSize: 16, 
              padding: '14px 18px 80px 18px', 
              textAlign: 'left',
              background: replyTo ? 'var(--glass-bg)' : 'var(--input-bg)',
              color: 'var(--text)',
              resize: 'vertical',
              boxSizing: 'border-box',
              borderTop: replyTo ? '0' : '2px solid var(--primary-light)',
              boxShadow: replyTo ? 'var(--shadow-md)' : 'var(--shadow-sm)',
              transition: 'all 0.2s ease',
              '&:focus': {
                outline: 'none',
                borderColor: 'var(--primary)',
                boxShadow: '0 0 0 2px var(--accent)'
              }
            }}
          />
        {mediaPreview && (
          <div style={{ marginTop: 8 }}>
            {mediaType === 'audio' ? (
              <audio src={mediaPreview} controls />
            ) : mediaType === 'video' ? (
              <video src={mediaPreview} controls style={{ maxWidth: 200 }} />
            ) : null}
            <button type="button" onClick={() => { setMediaFile(null); setMediaPreview(null); setMediaType(null); }} style={{ marginLeft: 8 }}>Remove</button>
          </div>
        )}
        <div style={{ 
          position: 'relative',
          marginTop: 12,
          width: '100%',
          marginBottom: 10
        }}>
          <div style={{ 
            position: 'absolute', 
            left: '10px', 
            bottom: '25px', 
            display: 'flex', 
            gap: 25, 
            alignItems: 'center',
            zIndex: 5
          }}>
            <button 
              type="button" 
              onClick={() => mediaInputRef.current.click()} 
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            >
              <FaPaperclip />
            </button>
            <input type="file" ref={mediaInputRef} style={{ display: 'none' }} accept="audio/*,video/*" onChange={e => {
              const file = e.target.files[0];
              if (file) {
                setMediaFile(file);
                setMediaType(file.type.startsWith('audio') ? 'audio' : file.type.startsWith('video') ? 'video' : null);
                setMediaPreview(URL.createObjectURL(file));
              }
            }} />
            <button 
              type="button" 
              onClick={() => startRecording('audio')} 
              disabled={isRecording}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            > 
              <FaMicrophone /> 
            </button>
            <button 
              type="button" 
              onClick={() => startRecording('video')} 
              disabled={isRecording}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            > 
              <FaVideo /> 
            </button>
            {isRecording && 
              <button 
                type="button" 
                onClick={stopRecording}
                style={{ background: 'var(--error)', border: 'none', color: 'var(--text-light)', padding: '4px 8px', borderRadius: 4, cursor: 'pointer' }}
              >
                Stop
              </button>
            }
          </div>
          <button 
            type="submit" 
            style={{ 
              position: 'absolute',
              right: '10px',
              bottom: '25px',
              background: 'var(--primary-light)', 
              color: 'white', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 16px', 
              cursor: 'pointer',
              fontWeight: 'bold',
              zIndex: 5
            }}
          >
            Send
          </button>
        </div>
      </form>
      </div>
      <div className="comment-modal-content">
        {renderComments(null, 0)}
      </div>
    </>
  );
};

export default Comments;